import React, { useEffect, useState } from "react";
import { waAnchorProps } from "../../lib/whatsapp";
import { toast } from "sonner";
import { X, Coffee, Minus, Plus, MessageCircle, Store, Loader2, ExternalLink, Check } from "lucide-react";
import { checkinApi } from "../../lib/checkinApi";
import { LANGS, FLAGS, NAMES } from "../../lib/breakfastI18n";

const iso = (d) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
const defaultDate = () => { const d = new Date(); if (d.getHours() >= 11) d.setDate(d.getDate() + 1); return iso(d); };

// Issue a "colazione al bar" voucher for a reservation and open WhatsApp (guest + optional bar notice).
export function BreakfastDialog({ item, onClose, onCreated }) {
  const [people, setPeople] = useState(2);
  const [date, setDate] = useState(defaultDate());
  const [lang, setLang] = useState("it");
  const [busy, setBusy] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const existing = (item.breakfast_vouchers || []).slice(-1)[0];
  const [loadingExisting, setLoadingExisting] = useState(!!existing?.id);

  useEffect(() => {
    if (!existing?.id) return;
    checkinApi.get(`/breakfast/vouchers/${existing.id}`, { params: { origin_url: window.location.origin } }).then((r) => setVoucher(r.data)).catch(() => {}).finally(() => setLoadingExisting(false));
  }, [existing?.id]);

  const markSent = (who) => {
    if (!voucher) return;
    setVoucher({ ...voucher, [who === "bar" ? "sent_bar_at" : "sent_guest_at"]: new Date().toISOString() });
    checkinApi.post(`/breakfast/vouchers/${voucher.id}/sent`, { who }).then(() => onCreated?.()).catch(() => {});
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await checkinApi.post("/breakfast/vouchers", { reservation_id: item.id, guest: item.guest, phone: item.phone || "", people, date, room: String(item.room?.number || ""), origin_url: window.location.origin, lang });
      setVoucher(data);
      toast.success(`Voucher ${data.code} creato`);
      onCreated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const changeLang = async (l) => {
    if (!voucher || voucher.lang === l) return;
    try {
      const { data } = await checkinApi.post(`/breakfast/vouchers/${voucher.id}/lang`, { lang: l }, { params: { origin_url: window.location.origin } });
      setVoucher(data);
      toast.success(`Voucher e messaggio WhatsApp in ${NAMES[l]}`);
    } catch { toast.error("Errore"); }
  };

  const LangToggle = ({ value, onChange }) => (
    <div className="flex rounded-full bg-white border border-[#e8dcc8] p-1 text-sm font-bold" data-testid="breakfast-lang-toggle">
      {LANGS.map((l) => (
        <button key={l} type="button" onClick={() => onChange(l)} title={NAMES[l]} className={`flex-1 h-9 rounded-full ${value === l ? "bg-[#4a1c17] text-white" : "text-[#7c5a4e]"}`} data-testid={`breakfast-lang-${l}`}>{FLAGS[l]} <span className="uppercase text-xs">{l}</span></button>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl bg-[#f7ebdf] p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="breakfast-dialog">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-bold inline-flex items-center gap-2"><Coffee className="h-5 w-5 text-[#C48B31]" /> Colazione al bar</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-white flex items-center justify-center" data-testid="breakfast-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="text-sm text-[#7c5a4e]">{item.guest} · Camera {item.room?.name}{item.phone ? ` · ${item.phone}` : " · telefono mancante (il link si apre senza destinatario)"}</div>

        {loadingExisting ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div> : !voucher ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-[#e8dcc8] bg-white px-3 h-12 flex items-center justify-between">
                <button onClick={() => setPeople(Math.max(1, people - 1))} className="h-8 w-8 rounded-full bg-[#faf3ee] flex items-center justify-center" data-testid="breakfast-dec"><Minus className="h-4 w-4" /></button>
                <span className="font-bold" data-testid="breakfast-people">{people} {people === 1 ? "persona" : "persone"}</span>
                <button onClick={() => setPeople(Math.min(20, people + 1))} className="h-8 w-8 rounded-full bg-[#faf3ee] flex items-center justify-center" data-testid="breakfast-inc"><Plus className="h-4 w-4" /></button>
              </div>
              <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-full h-12 rounded-2xl border border-[#e8dcc8] bg-white px-4 text-base" data-testid="breakfast-date" />
            </div>
            <div className="flex gap-2">
              {[0, 1].map((k) => { const d = new Date(); d.setDate(d.getDate() + k); const v = iso(d); return <button key={k} onClick={() => setDate(v)} className={`flex-1 h-10 rounded-full text-sm font-bold border ${date === v ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "bg-white border-[#e8dcc8]"}`} data-testid={`breakfast-day-${k}`}>{k === 0 ? "Oggi" : "Domani"}</button>; })}
            </div>
            <LangToggle value={lang} onChange={setLang} />
            <button onClick={create} disabled={busy} className="w-full h-12 rounded-2xl bg-[#C48B31] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="breakfast-create">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Coffee className="h-5 w-5" />} Crea voucher</button>
          </>
        ) : (
          <div className="space-y-3" data-testid="breakfast-created">
            <div className="rounded-2xl bg-white border border-[#e8dcc8] p-4 text-center">
              <div className="text-[11px] uppercase tracking-widest text-[#7c5a4e]">Voucher</div>
              <div className="font-mono text-3xl font-bold tracking-widest text-[#4a1c17]" data-testid="breakfast-code">{voucher.code}</div>
              <div className="text-sm text-[#7c5a4e]">{voucher.people} persone · {voucher.date.split("-").reverse().join("/")}{voucher.lang && voucher.lang !== "it" && ` · ${FLAGS[voucher.lang]} ${NAMES[voucher.lang]}`}</div>
              <a href={voucher.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[#4a1c17] underline" data-testid="breakfast-open-link"><ExternalLink className="h-3 w-3" /> Anteprima voucher</a>
            </div>
            <LangToggle value={voucher.lang || "it"} onChange={changeLang} />
            <a {...waAnchorProps(voucher.wa_url)} onClick={() => markSent("guest")} className={`w-full h-14 rounded-2xl text-white font-bold inline-flex items-center justify-center gap-2 ${voucher.sent_guest_at ? "bg-purple-600" : "bg-[#25D366]"}`} data-testid="breakfast-wa-guest">{voucher.sent_guest_at ? <Check className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />} {voucher.sent_guest_at ? "Inviato all'ospite ✓ · reinvia" : "Invia all'ospite su WhatsApp"}</a>
            {voucher.bar_wa_url && <a {...waAnchorProps(voucher.bar_wa_url)} onClick={() => markSent("bar")} className={`w-full h-12 rounded-2xl font-bold inline-flex items-center justify-center gap-2 ${voucher.sent_bar_at ? "bg-purple-600 text-white" : "bg-white border-2 border-[#C48B31] text-[#4a1c17]"}`} data-testid="breakfast-wa-bar">{voucher.sent_bar_at ? <Check className="h-5 w-5" /> : <Store className="h-5 w-5" />} {voucher.sent_bar_at ? "Bar avvisato ✓ · reinvia" : "Avvisa il bar (Romina)"}</a>}
            <button onClick={() => setVoucher(null)} className="w-full h-10 text-sm text-[#7c5a4e] underline" data-testid="breakfast-new">Nuovo voucher</button>
            <button onClick={onClose} className="w-full h-10 text-sm text-[#7c5a4e] underline" data-testid="breakfast-done">Chiudi</button>
          </div>
        )}
      </div>
    </div>
  );
}
